import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import * as Icon from 'react-feather';

import { setIsDialNumber } from '../../store/slices/dialSlice';
import { followUpStatusUpdate } from '../../store/slices/callingSlice';

// A single fixed id keeps the backlog to one toast. Toastify refuses to create a
// second toast with an id that is already displayed, so a re-fetch can never
// stack a duplicate pile on top of the one already on screen.
export const MISSED_FOLLOW_UP_TOAST_ID = 'missed-follow-up';

const MissedFollowUpToast = ({ items, dispatch, closeToast }) => {
  const [pending, setPending] = useState(items || []);
  const [isOpen, setIsOpen] = useState(false);

  // A re-fetch (process switch) pushes a fresh array in through toast.update.
  useEffect(() => {
    setPending(items || []);
    setIsOpen(false);
  }, [items]);

  const onDial = async (item) => {
    try {
      await dispatch(
        followUpStatusUpdate({
          follow_up_status: 1,
          id: item?.id
        })
      ).unwrap();
      dispatch(setIsDialNumber(item?.phone_number));

      // The agent is going into a call now — clear the toast off the dialer.
      closeToast();
    } catch (err) {
      console.error('Failed to update follow-up status:', err);
    }
  };

  return (
    <div className="follow-up-group">
      <div
        className="follow-up-group__head cursor-pointer"
        onClick={() => setIsOpen((prev) => !prev)}>
        <Icon.Clock size={26} className="text-white" />
        <div className="d-flex flex-column justify-content-start w-100">
          <strong style={{ fontWeight: 'bold' }}>{pending?.length} Follow Up pending</strong>
          <span className="follow-up-group__hint">{isOpen ? 'Hide list' : 'Click to view'}</span>
        </div>
        {isOpen ? <Icon.ChevronUp size={18} /> : <Icon.ChevronDown size={18} />}
      </div>

      {isOpen && (
        <div className="follow-up-group__list">
          {pending?.map((item) => (
            <div
              key={item?.id}
              className="follow-up-group__row"
              onClick={() => onDial(item)}
              title={item?.phone_number}>
              <span className="follow-up-group__number">{item?.phone_number}</span>
              <Icon.PhoneForwarded size={16} className="text-green" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

MissedFollowUpToast.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      phone_number: PropTypes.string
    })
  ),
  dispatch: PropTypes.func,
  closeToast: PropTypes.func
};

// Shows the missed follow-up backlog as one grouped, expandable toast.
// Replaces the previous one-toast-per-row loop that filled the screen height
// and had to be dismissed item by item.
export const showMissedFollowUpToast = (items, dispatch) => {
  if (!items?.length) return;

  const render = ({ closeToast }) => (
    <MissedFollowUpToast items={items} dispatch={dispatch} closeToast={closeToast} />
  );

  // Updating in place rather than dismiss-then-recreate: toastify still holds a
  // toast in its internal map while it plays the exit animation, so recreating
  // straight after a dismiss is silently dropped.
  if (toast.isActive(MISSED_FOLLOW_UP_TOAST_ID)) {
    toast.update(MISSED_FOLLOW_UP_TOAST_ID, { render });
    return;
  }

  toast(render, {
    toastId: MISSED_FOLLOW_UP_TOAST_ID,
    className: 'custom-follow-up-toast',
    position: 'top-right',
    // Hides itself after 10s. pauseOnHover keeps it alive while the agent is
    // actually reading or clicking through the list.
    autoClose: 10000,
    hideProgressBar: true,
    closeOnClick: false,
    pauseOnHover: true,
    draggable: false
  });
};

export default MissedFollowUpToast;
